import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import IdeLauncher from './IdeLauncher';

/** Copy to the clipboard, falling back for browsers and contexts without the async API.
 *
 * navigator.clipboard is undefined on plain http and in some embedded browsers, and a
 * copy button that silently does nothing is worse than one that is not there.
 */
async function copyText(value) {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch (error) {
    // fall through to the textarea approach
  }
  try {
    const area = document.createElement('textarea');
    area.value = value;
    area.style.position = 'fixed';
    area.style.opacity = '0';
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(area);
    return ok;
  } catch (error) {
    return false;
  }
}

function CopyRow({ label, value, secret = false }) {
  const [copied, setCopied] = useState(false);
  const [shown, setShown] = useState(false);

  if (!value) return null;

  const hidden = secret && !shown;

  return (
    <div className="lab-access-row">
      <span className="lab-access-label">{label}</span>
      <span className={`lab-access-value${hidden ? ' lab-access-value-hidden' : ''}`}>
        {hidden ? '••••••••••••' : value}
      </span>
      {secret && (
        <button type="button" className="lab-access-button" onClick={() => setShown(!shown)}>
          {shown ? 'Hide' : 'Show'}
        </button>
      )}
      <button
        type="button"
        className="lab-access-button"
        onClick={async () => {
          const ok = await copyText(value);
          setCopied(ok);
          if (ok) setTimeout(() => setCopied(false), 1500);
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

/** Everything the AWS sign-in page asks for, in a dialog that stays put.
 *
 * A dialog rather than a submenu inside the user menu, deliberately. The menu closes on
 * any click outside itself and sits in a hover-ish corner of the header, so a panel
 * inside it disappears at the first stray click - while this is the one screen a
 * student reads *and types from* at the same time, with AWS open in another tab.
 *
 * It is portalled to document.body and its open state is owned by the header, so the
 * menu closing behind it does not take it with it.
 *
 * The password is hidden until asked for. Trainers share their screen.
 */
/**
 * Which class the dialog opens on.
 *
 * A class is named `<Training>_<identifier>`, and the training pages live under
 * `/training/<Training>/...` - so on a training page, the class of that training is the one
 * the student is working in. Among those, a running one; failing that, any running class
 * (the backend lists running first); failing that, the first.
 */
// F-117. The same test as the admin console's StudentIdeControl: only a bare IP has the
// self-signed certificate a browser warns about.
export function warnsAboutCertificate(url) {
  try {
    return /^\d{1,3}(\.\d{1,3}){3}$/.test(new URL(url).hostname);
  } catch (error) {
    return false;
  }
}

export function initialClassIndex(classes, pathname = '') {
  const training = (pathname.match(/^\/training\/([^/?#]+)/) || [])[1];
  const ofTraining = (entry) => training && (entry.className || '').split('_')[0] === training;
  const running = (entry) => entry.classStatus === 'running';
  const pick = [(entry) => ofTraining(entry) && running(entry), (entry) => ofTraining(entry), running];
  for (const test of pick) {
    const index = classes.findIndex(test);
    if (index >= 0) return index;
  }
  return 0;
}

/** One class's sign-in details (E9-S16: a dialog can now hold several). */
function LabDetails({ lab, signInUrl, labInstance }) {
  return (
    <>
      {lab.className && (
        <p className="lab-dialog-class">
          Class <strong>{lab.className}</strong>
        </p>
      )}

      <CopyRow label="Account" value={lab.accountId} />
      <CopyRow label="User" value={lab.userName} />
      <CopyRow label="Password" value={lab.password} secret />

      {!lab.password && <p className="lab-access-note">Your trainer will give you the password.</p>}

      {/* A personal password needs no explanation - it is in the field above and
          it works. Only the shared one needs a sentence, because AWS will ask the
          student to change it the first time they sign in, and the value here then
          stops being the one they need. Saying so is what stops the panel going
          quietly stale on them. */}
      {lab.password && !lab.passwordIsPersonal && (
        <p className="lab-access-note">
          This is the initial password, shared by your class. AWS asks you to choose your own the first time you sign in
          — after that, use the one you chose, not this one.
        </p>
      )}

      <ol className="lab-dialog-steps">
        <li>Open the sign-in page below.</li>
        <li>Paste the account id if it isn't populated, then the user name and password.</li>
        {!lab.passwordIsPersonal && <li>Choose your own password when AWS asks.</li>}
      </ol>

      {/* Directly under the steps, because step 1 says "the sign-in page below"
          and it used to sit past the whole editor section - so "below" meant two
          headings further down, after the thing it was not talking about. */}
      <a href={signInUrl} className="lab-dialog-signin" target="_blank" rel="noopener noreferrer">
        Sign in to AWS
      </a>

      {/* E9-S10. Only some lab types deploy an IDE host, so a student on one that
          does not sees no section at all rather than a link that fails. The host
          publishes its own address on every boot, which is why there is no Elastic
          IP to pay for: the address changes whenever an idle host restarts (F-073)
          and a value recorded once would be wrong by the first restart. */}
      {lab.codeServerUrl && (
        <div className="lab-dialog-codeserver">
          <h3>Your IDE</h3>
          {/* The certificate line is for an IDE on its bare IP, whose self-signed
              certificate makes the browser warn: without it a student meets the warning
              and stops. Behind CloudFront the certificate is trusted, and promising a
              warning there would only teach students to click through real ones (F-117). */}
          {warnsAboutCertificate(lab.codeServerUrl) && (
            <p className="lab-access-note">
              Your browser will warn about the certificate — that is expected. Choose to continue.
            </p>
          )}
          {/* E9-S18: the machine's state, and an Open IDE that starts an idle one. */}
          <IdeLauncher lab={lab} labInstance={labInstance} />
        </div>
      )}
    </>
  );
}

function LabAccessDialog({ open, lab, labs = [], sandboxSignInUrl, labInstance, onClose }) {
  const closeRef = useRef(null);
  // E9-S16. Every active class, running first. An older caller passes only `lab`.
  const classes = labs.length > 0 ? labs : lab ? [lab] : [];
  const [chosen, setChosen] = useState(0);
  const current = classes[Math.min(chosen, classes.length - 1)] || null;
  const signInUrl = current?.signInUrl || sandboxSignInUrl;

  // Each time it opens, on the class that matters now: the running class of the training
  // the student is reading, else any running class, else the first (owner, 2026-09-26).
  const classKey = classes.map((entry) => `${entry.className}:${entry.classStatus}`).join('|');
  useEffect(() => {
    if (open) setChosen(initialClassIndex(classes, window.location.pathname));
    // classKey stands for `classes`, which is a new array on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, classKey]);

  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // The close button, not the first copy button: opening straight onto a control that
    // puts a password on the clipboard is not a good default.
    if (closeRef.current) closeRef.current.focus();
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="lab-dialog-overlay" onClick={onClose} role="presentation">
      <div
        className="lab-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="lab-dialog-title"
        // The overlay closes on click; the dialog must not, or selecting the password
        // with the mouse would dismiss the thing being read.
        onClick={(event) => event.stopPropagation()}
      >
        <div className="lab-dialog-header">
          <h2 id="lab-dialog-title">Your AWS sandbox</h2>
          <button type="button" ref={closeRef} className="lab-dialog-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="lab-dialog-body">
          {!signInUrl && <p>No sandbox account is assigned to you yet.</p>}

          {signInUrl && (
            <>
              {classes.length > 1 && (
                <div className="lab-dialog-classes" role="tablist" aria-label="Your classes">
                  {classes.map((entry, index) => (
                    <button
                      key={`${entry.className}-${entry.accountId}`}
                      type="button"
                      role="tab"
                      aria-selected={entry === current}
                      className={`lab-dialog-class-tab${entry === current ? ' is-selected' : ''}`}
                      onClick={() => setChosen(index)}
                    >
                      {entry.className || entry.accountId}
                      {entry.classStatus && entry.classStatus !== 'running' && (
                        <span className="lab-dialog-class-status"> · {entry.classStatus}</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {current ? (
                <LabDetails
                  key={`${current.className}-${current.accountId}`}
                  lab={current}
                  signInUrl={signInUrl}
                  labInstance={labInstance}
                />
              ) : null}
            </>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default LabAccessDialog;
